// Dibujo de la planta de la oficina, en rectángulos de píxel entero sobre «píxeles de arte» (la escena amplía por un
// zoom entero: nítido). Con la paleta ÚNICA de la oficina (ui/paleta.js, visual.md §2.A): la misma escalera de grises que
// la interfaz, ni un píxel de oro, ni color de sala (el color de la escena es del DOM: insignias, puntos, letreros).
// Por capas, para que la mesa tape las manos de quien se sienta detrás: `dibujarFondo` (suelo, paredes, salas, sillas y
// siluetas de las mesas vacías) → los monigotes (Lienzo.tsx) → `dibujarMesas` (las mesas con alguien o la libre).
import { silla } from '../../../lib/oficina/pixel-art';
import { escena, oficina } from '../ui/paleta.js';
import { PARED, PUESTO, ZOCALO, type MesaMundo, type Mundo, type Rect } from './generar';

type Ctx = CanvasRenderingContext2D;

/** Lado de una baldosa del suelo. */
const BALDOSA = 32;

const r = (ctx: Ctx, x: number, y: number, w: number, h: number, c: string) => {
  ctx.fillStyle = c;
  ctx.fillRect(Math.round(x), Math.round(y), Math.round(w), Math.round(h));
};

/** Suelo de baldosas de 32×32 a dos tonos casi iguales, sin juntas ni vetas: casi liso, pero sigue siendo un suelo. */
function suelo(ctx: Ctx, ancho: number, alto: number): void {
  r(ctx, 0, 0, ancho, alto, escena.suelo);
  for (let y = 0; y < alto; y += BALDOSA) {
    for (let x = ((y / BALDOSA) % 2) * BALDOSA; x < ancho; x += 2 * BALDOSA) r(ctx, x, y, BALDOSA, BALDOSA, escena.sueloAlt);
  }
}

/** Paredes lisas: la banda de arriba (con widgets, los widgets son la pared) y el zócalo con la puerta. */
function paredes(ctx: Ctx, m: Mundo): void {
  r(ctx, 0, 0, m.ancho, PARED, oficina.fondo);
  r(ctx, 0, m.alto - ZOCALO, m.ancho, ZOCALO, oficina.fondo);
  // La puerta: un hueco estrecho en el zócalo, un peldaño más claro, con su dintel.
  r(ctx, m.puerta.x - 8, m.puerta.y + 2, 16, ZOCALO - 2, oficina.elevada);
  r(ctx, m.puerta.x - 8, m.puerta.y + 2, 16, 1, oficina.control);
}

/** La sala: una superficie rellena sin borde, con las esquinas recortadas 2 px (el redondeo del píxel). */
function sala(ctx: Ctx, b: Rect, color: string = oficina.elevada): void {
  r(ctx, b.x, b.y + 2, b.w, b.h - 4, color);
  r(ctx, b.x + 2, b.y, b.w - 4, 2, color);
  r(ctx, b.x + 2, b.y + b.h - 2, b.w - 4, 2, color);
}

/** Centro de un puesto y la caja de su mesa. */
const tablero = (me: MesaMundo) => ({ cx: me.rect.x + me.rect.w / 2, x: me.rect.x + (me.rect.w - PUESTO.mesaAncho) / 2, y: me.rect.y + PUESTO.mesa });

/** Una mesa: tablero con su canto de luz, frente un peldaño más oscuro y el teclado del lado de la persona. */
function mesa(ctx: Ctx, me: MesaMundo): void {
  const { cx, x, y } = tablero(me);
  const w = PUESTO.mesaAncho;
  const alto = PUESTO.mesaAlto;
  r(ctx, x + 2, y + alto, w - 4, 2, escena.sombra);
  r(ctx, x, y, w, alto - 6, oficina.pulsado);
  r(ctx, x, y, w, 1, escena.mesaLuz);
  r(ctx, x, y + alto - 6, w, 6, oficina.control);
  r(ctx, cx - 9, y + 2, 18, 2, oficina.elevada); // el teclado, en el canto del lado de la persona
}

/** El fondo: suelo, paredes, salas, la silla de cada mesa con alguien y la silueta de las vacías (sin nada más). */
export function dibujarFondo(ctx: Ctx, m: Mundo): void {
  suelo(ctx, m.ancho, m.alto);
  paredes(ctx, m);
  for (const z of m.zonas) sala(ctx, z.rect);
  // 04-11 — la placa de cada grupo con nombre (un peldaño sobre la sala, misma forma); «Sin grupo» no lleva placa.
  for (const g of m.grupos) if (g.grupoId) sala(ctx, g.placa, escena.isla);
  for (const me of m.mesas) {
    const { cx, x, y } = tablero(me);
    if (me.agentId) silla(ctx, cx, me.rect.y + PUESTO.silla);
    else r(ctx, x, y, PUESTO.mesaAncho, PUESTO.mesaAlto, me.grupoId ? escena.huecoIsla : escena.mesaVacia);
  }
}

/** Las mesas de quien está sentado, por encima de su monigote. */
export function dibujarMesas(ctx: Ctx, m: Mundo): void {
  for (const me of m.mesas) if (me.agentId) mesa(ctx, me);
}
